import { useId, type ReactNode } from 'react'

import { cn } from 'cn'
import { Label } from '#ui/components/label.tsx'

/**
 * A labeled form control, with its help text and **its error next to it**.
 *
 * The console has twenty-five controls and had no inline errors at all: every form surfaced one
 * server message at the bottom, under the submit button. The provision form has eight fields, so a
 * rejected slug produced a sentence somewhere below the fold with nothing marking which input it
 * was about. That is WCAG 3.3.1, and it is also just how a form gets abandoned.
 *
 * Three things this wires up that are easy to do by hand and easy to do wrong:
 *
 * **One id, generated.** `useId` rather than a prop, so no caller has to invent unique ids and no
 * two fields can collide — a duplicate id silently points two labels at one input.
 *
 * **`aria-describedby` names the help *and* the error**, in that order, and omits whichever is
 * absent. Passing a dangling id for an element that is not rendered is the common bug: the control
 * then describes itself as pointing at nothing.
 *
 * **`aria-invalid` is driven by the error, not set separately.** Two props that can disagree is
 * how a field ends up outlined in red while announcing itself as valid.
 *
 * The control is a render prop rather than `children`, because it needs the generated id and the
 * ARIA attributes, and threading them through `cloneElement` would break the moment somebody wraps
 * their input in a div.
 */
export function Field({
  label,
  help,
  error,
  meta,
  aside,
  required = false,
  className,
  children,
}: {
  label: ReactNode
  /** Rendered under the control. What the field is for, or the rule it must satisfy. */
  help?: ReactNode
  /** When present the field is invalid, and this is why. One sentence, naming the fix. */
  error?: ReactNode
  meta?: ReactNode
  aside?: ReactNode
  required?: boolean
  className?: string
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean
    required: boolean
  }) => ReactNode
}) {
  const id = useId()
  const helpId = `${id}-help`
  const errorId = `${id}-error`

  const describedBy =
    [help === undefined ? null : helpId, error === undefined ? null : errorId]
      .filter((value) => value !== null)
      .join(' ') || undefined

  const labelElement = (
    <Label htmlFor={id}>
      {label}
      {required ? (
        /*
         * A real word, not a bare asterisk. An asterisk is announced as "star" or skipped
         * entirely, so the one thing a screen-reader user needs to know about a field is the
         * one thing that does not reach them. Carried over from the CMS field editor, which
         * had already got this right.
         */
        <span className="text-muted-foreground text-xs leading-none font-normal">(required)</span>
      ) : null}
    </Label>
  )

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {aside === undefined ? (
        labelElement
      ) : (
        <div className="flex items-center justify-between gap-3">
          {labelElement}
          <div className="text-muted-foreground flex shrink-0 items-center text-xs leading-none tabular-nums">
            {aside}
          </div>
        </div>
      )}

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error !== undefined,
        required,
      })}

      {help === undefined && meta === undefined ? null : (
        <div className="flex items-start gap-3">
          {help === undefined ? null : (
            <p id={helpId} className="text-muted-foreground min-w-0 flex-1 text-xs leading-relaxed">
              {help}
            </p>
          )}
          {meta === undefined ? null : (
            <div className="text-muted-foreground ml-auto shrink-0 text-xs tabular-nums">
              {meta}
            </div>
          )}
        </div>
      )}

      {error === undefined ? null : (
        /*
         * `role="alert"` so it is announced when it appears. A validation failure is the one thing
         * on a form worth interrupting for — the submit did not happen, and the user is already
         * moving on.
         */
        <p id={errorId} role="alert" className="text-destructive text-xs font-medium">
          {error}
        </p>
      )}
    </div>
  )
}
